import React, { useEffect, useRef, useState } from "react";
import { TextField, Button } from "@mui/material";
import TimeRangeFilter from "./TimeRangeFilter";
import StaffFilter from "./StaffFilter";
import { FilterOptions } from "../../../../types/order";
import { StaffMember } from "src/shared/services/interfacev2";

interface FilterDropdownProps {
  filters: FilterOptions;
  staffList: StaffMember[];
  onFilterChange: (key: keyof FilterOptions, value: string) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
  parentRef?: React.RefObject<HTMLDivElement>; // 👈 get width of SearchBar container
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ filters, staffList, onFilterChange, onApply, onReset, onClose, parentRef }) => {
  const [width, setWidth] = useState<number>(420);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 👇 match width dynamically with parent (search bar container)
  useEffect(() => {
    if (parentRef?.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setWidth(rect.width);
    }
  }, [parentRef]);

  const handleClear = () => {
    onReset();
    onClose();
  };

  const handleSearch = () => {
    onApply();
    onClose();
  };

  const now = new Date();
  const today = now.getFullYear() + "-" +  String(now.getMonth() + 1).padStart(2, "0") + "-" +  String(now.getDate()).padStart(2, "0");


  return (
    <div
      ref={dropdownRef}
      className="absolute top-full mt-2 bg-white rounded-2xl shadow-xl p-5 z-[1200] flex flex-col gap-5 border border-gray-100 animate-[fadeIn_0.2s_ease-in-out]"
      style={{
        width: `${width}px`,
        animation: "fadeIn 0.2s ease-in-out",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-base font-semibold text-gray-800">Filters</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition">
          ✕
        </button>
      </div>

      {/* Time Range Filter */}
      <div>
        <TimeRangeFilter timeRange={filters.timeRange} onTimeRangeChange={(value) => onFilterChange("timeRange", value)} />
      </div>

      {/* Staff Filter */}
      <div>
        <StaffFilter staffList={staffList} selectedStaff={filters.selectedStaff} onStaffChange={(value) => onFilterChange("selectedStaff", value)} />
      </div>

      {/* Custom Date Range */}
      {filters.timeRange === "CustomizeDate" && (
        <div className="flex flex-col gap-3">
          <TextField
            label="Start Date"
            type="date"
            value={filters.customStartDate}
            onChange={(e) => onFilterChange("customStartDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={filters.customEndDate}
            onChange={(e) => onFilterChange("customEndDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            inputProps={{
              min: filters.customStartDate,
              max: today,
            }}
          />
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 mt-2">
        <Button variant="outlined" color="secondary" onClick={handleClear} fullWidth className="!text-gray-700 !border-gray-300 hover:!bg-gray-100 !rounded-lg !capitalize">
          Clear
        </Button>
        <Button variant="contained" color="primary" onClick={handleSearch} fullWidth className="!rounded-lg !capitalize">
          Search
        </Button>
      </div>
    </div>
  );
};

export default FilterDropdown;
